import React from "react";
import { Link } from "react-router-dom";
import { Dropdown, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";

import CustomPagination from "../../../../../../core/common/customPagination/CustomPagination";
import { all_routes } from "../../../../../router/all_routes";
import {
  getColorFromName,
  getInitials,
} from "../../../../../../helper/avatarHelper";
import { humanReadableDate } from "../../../../../../helper/dateTimeHelper";
import { STATUS_COLORS } from "../constants/adminLeaves.constants";
import { capitalize, formatDayPart } from "../helpers/adminLeaves.helper";

import type {
  AdminLeavesFilters,
  AdminLeavesPagination,
  LeaveHistory,
  LeaveStatus,
} from "../../../../../../core/data/redux/admin-leaves/types";

interface AdminLeavesTableProps {
  leaves: LeaveHistory[];
  loading: boolean;
  pagination: AdminLeavesPagination;
  filters: AdminLeavesFilters;
  onFilterChange: (partial: Partial<AdminLeavesFilters>) => void;
  onView: (leave: LeaveHistory) => void;
  onAction: (leave: LeaveHistory, status: LeaveStatus) => void;
  onShowReason: (reason: string) => void;
}

const REASON_PREVIEW_LIMIT = 70;

const AdminLeavesTable: React.FC<AdminLeavesTableProps> = ({
  leaves,
  loading,
  pagination,
  filters,
  onFilterChange,
  onView,
  onAction,
  onShowReason,
}) => {
  const getActionItems = (leave: LeaveHistory): MenuProps["items"] => {
    const items: MenuProps["items"] = [
      {
        key: "view",
        icon: <i className="ti ti-eye" style={{ color: "#3b82f6" }} />,
        label: "View",
        onClick: () => onView(leave),
      },
    ];

    if (leave.status === "pending") {
      items.push({
        key: "approve",
        icon: <i className="ti ti-check" style={{ color: "#16a34a" }} />,
        label: "Approve",
        onClick: () => onAction(leave, "approved"),
      });
      items.push({
        key: "reject",
        icon: <i className="ti ti-x" style={{ color: "#dc2626" }} />,
        label: "Reject",
        onClick: () => onAction(leave, "rejected"),
      });
    } else if (leave.status === "approved") {
      items.push({
        key: "pending",
        icon: (
          <i className="ti ti-arrow-back-up" style={{ color: "#f59e0b" }} />
        ),
        label: "Mark Pending",
        onClick: () => onAction(leave, "pending"),
      });
      items.push({
        key: "cancel",
        icon: <i className="ti ti-ban" style={{ color: "#6b7280" }} />,
        label: "Cancel",
        onClick: () => onAction(leave, "cancelled"),
      });
    } else if (leave.status === "rejected") {
      items.push({
        key: "pending",
        icon: (
          <i className="ti ti-arrow-back-up" style={{ color: "#f59e0b" }} />
        ),
        label: "Mark Pending",
        onClick: () => onAction(leave, "pending"),
      });
    }

    return items;
  };

  const columns: ColumnsType<LeaveHistory> = [
    {
      title: "Sr No.",
      key: "srNo",
      width: 70,
      align: "center",
      fixed: "left",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
          {(filters.page - 1) * filters.limit + index + 1}
        </span>
      ),
    },
    {
      title: "Employee",
      dataIndex: "userName",
      key: "userName",
      fixed: "left",
      width: 240,
      render: (text: string, record) => {
        const initials = getInitials(text);
        const bgColor = getColorFromName(text);
        return (
          <Link
            to={all_routes.empLeaveSummaryForAdmin.replace(
              ":empId",
              String(record.userId),
            )}
            style={{ color: "inherit", textDecoration: "none" }}>
            <div className="d-flex align-items-center">
              <div
                className="border avatar-rounded position-relative overflow-hidden"
                style={{ width: 40, height: 40, borderRadius: "50%" }}>
                <div
                  className="d-flex align-items-center justify-content-center w-100 h-100"
                  style={{
                    backgroundColor: bgColor,
                    color: "white",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                  }}>
                  {initials}
                </div>
                {record.image && (
                  <img
                    src={record.image}
                    alt={text}
                    className="position-absolute top-0 start-0 object-fit-cover"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </div>
              <div className="ms-2">
                <h6
                  className="fw-medium mb-1"
                  style={{ fontSize: 13.5, color: "#0f172a" }}>
                  {text}
                </h6>
                {record.department && (
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {record.department}
                  </span>
                )}
              </div>
            </div>
          </Link>
        );
      },
    },
    {
      title: "Leave Type",
      dataIndex: "leaveTypeName",
      key: "leaveTypeName",
      width: 140,
      render: (text: string, record) => (
        <div className="d-flex flex-column">
          <span className="fw-medium" style={{ fontSize: 13 }}>
            {text || "—"}
          </span>
          {record.leaveTypeUnlimited && (
            <Tag
              color="purple"
              style={{
                fontSize: 10.5,
                margin: "2px 0 0",
                width: "fit-content",
              }}>
              Unlimited
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "From",
      dataIndex: "startDate",
      key: "startDate",
      width: 140,
      render: (text: string, record) => (
        <Tooltip title={humanReadableDate(text)}>
          <div className="d-flex flex-column">
            <span className="fw-medium" style={{ fontSize: 13 }}>
              {humanReadableDate(text)}
            </span>
            <span
              className="bg-transparent-purple text-purple px-1"
              style={{ fontSize: 11, width: "fit-content", borderRadius: 4 }}>
              {formatDayPart(record.startDayPart)}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "To",
      dataIndex: "endDate",
      key: "endDate",
      width: 140,
      render: (text: string, record) => (
        <Tooltip title={humanReadableDate(text)}>
          <div className="d-flex flex-column">
            <span className="fw-medium" style={{ fontSize: 13 }}>
              {humanReadableDate(text)}
            </span>
            <span
              className="bg-transparent-purple text-purple px-1"
              style={{ fontSize: 11, width: "fit-content", borderRadius: 4 }}>
              {formatDayPart(record.endDayPart)}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Days",
      dataIndex: "days",
      key: "days",
      width: 100,
      align: "center",
      render: (days: number) => (
        <Tag color="blue" style={{ fontSize: 11.5, margin: 0 }}>
          {`${days} Day${days > 1 ? "s" : ""}`}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: LeaveStatus) => (
        <Tag
          color={STATUS_COLORS[status] || "default"}
          style={{ fontSize: 11.5, margin: 0 }}>
          {capitalize(status)}
        </Tag>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 200,
      render: (reason: string) => {
        if (!reason) {
          return <span style={{ color: "#94a3b8" }}>—</span>;
        }
        const isLong = reason.length > REASON_PREVIEW_LIMIT;
        const preview = isLong
          ? `${reason.slice(0, REASON_PREVIEW_LIMIT).trim()}…`
          : reason;
        return (
          <div style={{ maxWidth: 200 }}>
            <span
              style={{
                fontSize: 12.5,
                color: "#475569",
                lineHeight: 1.5,
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}>
              {preview}
            </span>
            {isLong && (
              <button
                type="button"
                className="btn btn-link p-0 ms-1"
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  verticalAlign: "baseline",
                  textDecoration: "none",
                }}
                onClick={() => onShowReason(reason)}>
                Read more
              </button>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      align: "center",
      fixed: "right",
      render: (_, leave) => (
        <Dropdown
          menu={{ items: getActionItems(leave) }}
          trigger={["click"]}
          placement="bottomRight">
          <button
            type="button"
            className="btn btn-icon btn-sm btn-light"
            style={{ borderRadius: 6 }}
            onClick={(e) => e.stopPropagation()}>
            <i className="ti ti-dots-vertical" />
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="card-body p-0 mb-3 border-0">
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{
            minHeight: 400,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
          }}>
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            />
            <p className="mt-3 text-muted">Loading leaves...</p>
          </div>
        </div>
      ) : leaves.length > 0 ? (
        <div
          className="bg-white"
          style={{
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
          }}>
          <Table
            className="custom-data-table"
            columns={columns}
            dataSource={leaves}
            rowKey="id"
            size="middle"
            pagination={false}
            scroll={{ x: 1500 }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
            }
          />
          <CustomPagination
            currentPage={pagination.current_page || filters.page}
            totalPages={pagination.total_pages || 1}
            total={pagination.total}
            limit={pagination.per_page || filters.limit}
            hasNextPage={
              (pagination.current_page || filters.page) <
              (pagination.total_pages || 1)
            }
            hasPrevPage={(pagination.current_page || filters.page) > 1}
            onPageChange={(page) => onFilterChange({ page })}
            onLimitChange={(limit) => onFilterChange({ limit, page: 1 })}
          />
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{
            minHeight: 400,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
          }}>
          <div className="text-center">
            <i
              className="ti ti-calendar-off"
              style={{ fontSize: 48, color: "#cbd5e1" }}
            />
            <h5 className="mt-3 mb-2 fw-semibold">No leave records found</h5>
            <p className="text-muted mb-0">Try adjusting your filters.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeavesTable;
